import React from 'react';
import { BookOutlinedIcon, InfoCircleFilledIcon } from 'icons';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';

import { useAppDispatch, useAppSelector } from 'store';

import { Payment } from 'modules/course-detail';
import { lessonConst, PaymentModal, setLessonStatus, useLessonDetail, useLessonTest } from 'modules/lesson-detail';

import { VideoPlayer, Visible } from 'components';

import { InfoSection } from './info-section';
import { PaymentSkeleton } from './payment-skeleton';
import { Result } from './result';
import { TestSection } from './test-section';

export const Main = () => {
  const t = useTranslations();
  const { query } = useRouter();
  const dispatch = useAppDispatch();
  const { lesson, isLoading } = useLessonDetail(query?.lesson as string);
  const { refetchLessonTest } = useLessonTest(query?.lesson as string);
  const status = useAppSelector(({ lesson }) => lesson.status);

  const onSeen = () => {
    refetchLessonTest();
    dispatch(setLessonStatus(lessonConst.LessonStatus.SEEN));
  };

  const sections: Record<lessonConst.LessonStatus, JSX.Element> = {
    [lessonConst.LessonStatus.UNSEEN]: (
      <>
        <div className="h-14.5 flex w-full items-center justify-between rounded-xl bg-green-100 p-3">
          <p className="text-sm font-medium">{t('videoni_toliq_korib_chiqdingizmi')}</p>
          <div className="flex items-center gap-2">
            <button className="cursor-pointer rounded-xl bg-green-500 px-5 py-2 text-sm text-white" onClick={onSeen}>
              {t('ha')}
            </button>
          </div>
        </div>

        <div className="h-57 flex flex-col items-center justify-center space-y-3 rounded-xl bg-white text-gray-500">
          <InfoCircleFilledIcon />
          <p className="max-w-55 text-center text-xs font-medium">
            {t('videoni_toliq_tugatsangiz_bu_yerda_testlar_paydo_boladi')}
          </p>
        </div>
      </>
    ),
    [lessonConst.LessonStatus.SEEN]: <TestSection />,
    [lessonConst.LessonStatus.RESOLVED]: <Result isBought={lesson?.course?.is_bought} />,
    [lessonConst.LessonStatus.REJECTED]: <Result isBought={lesson?.course?.is_bought} />
  };

  return (
    <div className="mt-15 max-w-172.5 mx-auto space-y-5 px-4 lg:px-0">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2 text-lg font-semibold">
          <span>{t('mavzu')}:</span>
          <Visible if={!isLoading} else={<span className="h-4 w-40 animate-pulse rounded-md bg-gray-200" />}>
            <span>{lesson?.title}</span>
          </Visible>
        </div>
        {lesson?.course?.book_resource ? (
          <Link
            href={lesson?.course?.book_resource}
            target="_blank"
            className="hidden h-8 cursor-pointer items-center gap-2 rounded-lg border border-gray-300 bg-white px-2.5 text-sm font-medium text-gray-800 sm:flex"
          >
            <BookOutlinedIcon className="size-4" />
            <span>{t('kitobni_korish')}</span>
          </Link>
        ) : null}
      </div>
      <div className="space-y-3">
        <Visible if={!isLoading} else={<div className="sm:h-100 h-59 animate-pulse rounded-xl bg-gray-200" />}>
          <VideoPlayer
            src={lesson?.source as string}
            className="sm:!h-100 !h-59 rounded-xl object-cover"
            controls
          />
        </Visible>
        <Visible
          if={lesson?.has_test}
          else={
            <InfoSection
              desc={lesson?.description}
              nextLessonSlug={lesson?.next?.slug}
              isBought={lesson?.course?.is_bought}
            />
          }
        >
          {sections[status as lessonConst.LessonStatus]}
        </Visible>
        <Visible
          if={isLoading}
          else={
            <Visible if={!lesson?.course?.is_bought}>
              <Payment discountPrice={lesson?.course?.discount_price} price={lesson?.course?.price} />
            </Visible>
          }
        >
          <PaymentSkeleton />
        </Visible>
      </div>
      <PaymentModal discountPrice={lesson?.course?.discount_price} price={lesson?.course?.price} />
    </div>
  );
};
